import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import api from '../api/api';
import '../styles/EditCenterPage.css'; // Base styles
import '../styles/CenterDetailsPage.css'; // Additional styles for details page

const CenterDetailsPage = () => {
  const { centerId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [center, setCenter] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Check if user has permission (super_admin only)
  useEffect(() => {
    if (!user || user.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }

    fetchCenterData();
  }, [user, navigate, centerId]);

  const fetchCenterData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use the correct ID from URL params
      const response = await api.get(`/centers/${centerId}`);
      
      if (response.data) {
        // Set center data
        setCenter(response.data);
        
        
        // Load center classes/halaqas with student counts
        try {
          const classesResponse = await api.get(`/halaqas?center=${centerId}`);
          const halaqas = classesResponse.data.data || [];
          
          // Get student count for each class using multi-class support
          const classesWithCounts = await Promise.all(
            halaqas.map(async (halaqa) => {
              try {
                // Use multi-class query pattern from documentation
                const studentsResponse = await api.get(`/users?role=student&center=${centerId}`);
                const allStudents = studentsResponse.data || [];
                
                // Count students in this class (both halaqaId and halaqas array)
                const studentCount = allStudents.filter(student => {
                  const studentClasses = [];
                  if (student.studentInfo?.halaqaId) {
                    studentClasses.push(student.studentInfo.halaqaId.toString());
                  }
                  if (student.studentInfo?.halaqas) {
                    studentClasses.push(...student.studentInfo.halaqas.map(id => id.toString()));
                  }
                  return studentClasses.includes(halaqa._id.toString());
                }).length;
                
                return {
                  ...halaqa,
                  students: { length: studentCount }
                };
              } catch (err) {
                console.error(`Error counting students for class ${halaqa._id}:`, err);
                return { ...halaqa, students: { length: 0 } };
              }
            })
          );
          
          setClasses(classesWithCounts);
        } catch (err) {
          console.error('Error fetching center classes:', err);
        }
      }
    } catch (err) {
      console.error('Error fetching center data:', err);
      setError('Failed to load center data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Format date to readable string
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <div className="edit-center-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading center data...</p>
        </div>
      </div>
    );
  }

  if (!center) {
    return (
      <div className="edit-center-container">
        <div className="header">
          <div className="header-left">
            <Link to="/dashboard" className="back-link">
              &larr; Back to Dashboard
            </Link>
            <h1>Center Not Found</h1>
          </div>
        </div>
        <div className="error-message">The requested center could not be found.</div>
      </div>
    );
  }

  return (
    <div className="edit-center-container">
      <div className="header">
        <div className="header-left">
          <Link to="/dashboard" className="back-link">
            &larr; Back to Dashboard
          </Link>
          <h1>Center Details: {center.name}</h1>
        </div>
        <div className="header-actions">
          <Link to={`/center/edit/${center._id}`} className="edit-button">
            Edit Center
          </Link>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      <div className="center-details-content">
        <div className="details-section">
          <h2>Basic Information</h2>
          <div className="detail-row">
            <div className="detail-label">Center Name:</div>
            <div className="detail-value">{center.name}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">Center Code:</div>
            <div className="detail-value">{center.centerCode}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">Address:</div>
            <div className="detail-value">{center.address || 'Not provided'}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">Contact Email:</div>
            <div className="detail-value">{center.contactEmail || 'Not provided'}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">Contact Phone:</div>
            <div className="detail-value">{center.contactPhone || 'Not provided'}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">Created At:</div>
            <div className="detail-value">{formatDate(center.createdAt)}</div>
          </div>
        </div>
        
        {center.logoUrl && (
          <div className="details-section">
            <h2>Center Logo</h2>
            <div className="center-logo-display">
              <img src={center.logoUrl} alt="Center Logo" className="center-logo-image" />
            </div>
          </div>
        )}
        
        <div className="details-section">
          <h2>License Information</h2>
          <div className="detail-row">
            <div className="detail-label">License Level:</div>
            <div className="detail-value">{center.licenseLevel || 'Free'}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">License Start Date:</div>
            <div className="detail-value">{formatDate(center.licenseStartDate)}</div>
          </div>
          <div className="detail-row">
            <div className="detail-label">License End Date:</div>
            <div className="detail-value">{formatDate(center.licenseEndDate)}</div>
          </div>
        </div>
        
        {center.subdomainPrefix && (
          <div className="details-section">
            <h2>Branding Information</h2>
            <div className="detail-row">
              <div className="detail-label">Subdomain:</div>
              <div className="detail-value">{center.subdomainPrefix}.memoraize.io</div>
            </div>
            {center.brandingSettings && (
              <>
                <div className="detail-row">
                  <div className="detail-label">Primary Color:</div>
                  <div className="detail-value">
                    <span 
                      className="color-preview" 
                      style={{backgroundColor: center.brandingSettings.primaryColor}}
                    >
                      {center.brandingSettings.primaryColor}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Secondary Color:</div>
                  <div className="detail-value">
                    <span 
                      className="color-preview" 
                      style={{backgroundColor: center.brandingSettings.secondaryColor, color: '#fff'}}
                    >
                      {center.brandingSettings.secondaryColor}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Accent Color:</div>
                  <div className="detail-value">
                    <span 
                      className="color-preview" 
                      style={{backgroundColor: center.brandingSettings.accentColor}}
                    >
                      {center.brandingSettings.accentColor}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        
        
        <div className="details-section">
          <h2>Classes ({classes.length})</h2>
          {classes.length > 0 ? (
            <div className="classes-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Students</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {classes.map(cls => (
                    <tr key={cls._id}>
                      <td>{cls.name}</td>
                      <td>{cls.students?.length || 0}</td>
                      <td>
                        <Link to={`/classes/${cls._id}`}>View</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-data">No classes found for this center</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CenterDetailsPage;